import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { fmtDate, toInputDate } from '../utils';
import { Badge, Modal, Pagination, ConfirmDialog, Money, PursuitScore } from '../components/ui';
import { useAuth } from '../context/AuthContext';

const STAGES = ['Prospecting','Qualification','Shaping','Pre-RFP','Proposal','Evaluation','Won','Lost','No Bid','On Hold'];
const STAGE_COLORS = { Prospecting:'#8b5cf6',Qualification:'#3b82f6',Shaping:'#06b6d4','Pre-RFP':'#f59e0b',Proposal:'#f97316',Evaluation:'#ec4899',Won:'#16a34a',Lost:'#dc2626','No Bid':'#94a3b8','On Hold':'#64748b' };
const OPP_TYPES = ['New','Recompete','Expansion','Other'];
const CATEGORIES = ['Full & Open','Small Business','8a','WOSB','HubZone','SDVOSB','Unknown'];
const PRIORITIES = ['Critical','High','Medium','Low'];
const SOURCES = ['Existing Customer','Partner','SAM.gov','Agency Contact','Internal Lead','Event','Other'];
const SERVICE_AREAS = ['Appian','ServiceNow','Cloud','Other'];

export function OppForm({ opp, onSave, onClose }) {
  const [form, setForm] = useState({ name:'',opp_type:'New',description:'',category:'Unknown',priority:'Medium',source:'Internal Lead',service_area:'Other',prime:'TBD',teaming_partner:'',workshare_pct:'',forecasted_tcv:'',incumbent_info:'',risks_blockers:'',involves_ai:false,account_id:'',contact_id:'',stage:'Prospecting',probability:0,expected_close_date:'', ...opp, involves_ai: opp?.involves_ai == 1 });
  const [accounts, setAccounts] = useState([]); const [contacts, setContacts] = useState([]);
  const [saving, setSaving] = useState(false); const [error, setError] = useState('');
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const setCheck = k => e => setForm(f => ({ ...f, [k]: e.target.checked }));

  useEffect(() => {
    api.get('/accounts', { params: { limit:200 } }).then(r => setAccounts(r.data.data));
    api.get('/contacts', { params: { limit:200 } }).then(r => setContacts(r.data.data));
  }, []);

  const handleSave = async () => {
    if (!form.name) return setError('Name required');
    setSaving(true);
    try {
      const r = opp?.id ? await api.put(`/opportunities/${opp.id}`, form) : await api.post('/opportunities', form);
      onSave(r.data);
    } catch(e) { setError(e.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={opp?.id ? 'Edit Opportunity' : 'New Opportunity'} onClose={onClose} size="modal-lg">
      <div className="modal-body">
        {error && <div className="error-msg">{error}</div>}
        <div className="section-divider">Basic Info</div>
        <div className="form-group"><label className="form-label">Opportunity Name *</label><input className="form-input" value={form.name} onChange={set('name')} /></div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Opp Type</label>
            <select className="form-select" value={form.opp_type} onChange={set('opp_type')}>{OPP_TYPES.map(t => <option key={t}>{t}</option>)}</select>
          </div>
          <div className="form-group"><label className="form-label">Priority</label>
            <select className="form-select" value={form.priority} onChange={set('priority')}>{PRIORITIES.map(p => <option key={p}>{p}</option>)}</select>
          </div>
        </div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Category</label>
            <select className="form-select" value={form.category} onChange={set('category')}>{CATEGORIES.map(c => <option key={c}>{c}</option>)}</select>
          </div>
          <div className="form-group"><label className="form-label">Source</label>
            <select className="form-select" value={form.source} onChange={set('source')}>{SOURCES.map(s => <option key={s}>{s}</option>)}</select>
          </div>
        </div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Service Area</label>
            <select className="form-select" value={form.service_area} onChange={set('service_area')}>{SERVICE_AREAS.map(s => <option key={s}>{s}</option>)}</select>
          </div>
          <div className="form-group"><label className="form-label">Stage</label>
            <select className="form-select" value={form.stage} onChange={set('stage')}>{STAGES.map(s => <option key={s}>{s}</option>)}</select>
          </div>
        </div>
        <div className="form-group"><label className="form-label">Description</label><textarea className="form-textarea" value={form.description} onChange={set('description')} rows={2} /></div>

        <div className="section-divider">Financials & Teaming</div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Forecasted TCV ($)</label><input className="form-input" type="number" value={form.forecasted_tcv} onChange={set('forecasted_tcv')} /></div>
          <div className="form-group"><label className="form-label">Expected Close Date</label><input className="form-input" type="date" value={toInputDate(form.expected_close_date)} onChange={set('expected_close_date')} /></div>
        </div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Prime</label>
            <select className="form-select" value={form.prime} onChange={set('prime')}>
              <option value="Yes">Yes</option><option value="No">No</option><option value="TBD">TBD</option>
            </select>
          </div>
          <div className="form-group"><label className="form-label">Probability (%)</label><input className="form-input" type="number" min="0" max="100" value={form.probability} onChange={set('probability')} /></div>
        </div>
        {form.prime === 'No' && (
          <div className="form-grid">
            <div className="form-group"><label className="form-label">Teaming Partner</label><input className="form-input" value={form.teaming_partner} onChange={set('teaming_partner')} /></div>
            <div className="form-group"><label className="form-label">Workshare %</label><input className="form-input" type="number" value={form.workshare_pct} onChange={set('workshare_pct')} /></div>
          </div>
        )}

        <div className="section-divider">Relationships</div>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Account</label>
            <select className="form-select" value={form.account_id} onChange={set('account_id')}>
              <option value="">Select account...</option>{accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div className="form-group"><label className="form-label">Contact</label>
            <select className="form-select" value={form.contact_id} onChange={set('contact_id')}>
              <option value="">Select contact...</option>{contacts.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name}</option>)}
            </select>
          </div>
        </div>

        <div className="section-divider">Additional Details</div>
        <div className="form-group"><label className="form-label">Incumbent Info</label><textarea className="form-textarea" value={form.incumbent_info} onChange={set('incumbent_info')} rows={2} /></div>
        <div className="form-group"><label className="form-label">Risks / Blockers</label><textarea className="form-textarea" value={form.risks_blockers} onChange={set('risks_blockers')} rows={2} /></div>
        <div style={{ display:'flex',alignItems:'center',gap:10,padding:'8px 0' }}>
          <input type="checkbox" id="ai_flag" checked={!!form.involves_ai} onChange={setCheck('involves_ai')} style={{ width:16,height:16 }} />
          <label htmlFor="ai_flag" style={{ fontSize:13,fontWeight:500,cursor:'pointer' }}>🤖 This opportunity involves AI</label>
        </div>
      </div>
      <div className="modal-footer">
        <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save Opportunity'}</button>
      </div>
    </Modal>
  );
}

function PipelineView({ opps, navigate }) {
  const byStage = {};
  STAGES.forEach(s => { byStage[s] = []; });
  opps.forEach(o => { if (byStage[o.stage]) byStage[o.stage].push(o); });
  return (
    <div className="pipeline-board">
      {STAGES.map(stage => {
        const items = byStage[stage];
        const total = items.reduce((s, o) => s + (o.forecasted_tcv || 0), 0);
        return (
          <div key={stage} className="pipeline-col">
            <div className="pipeline-col-header" style={{ borderTop: `3px solid ${STAGE_COLORS[stage]}` }}>
              <div className="pipeline-col-title">{stage}</div>
              <div className="pipeline-col-meta">{items.length} · ${(total/1000).toFixed(0)}k</div>
            </div>
            <div className="pipeline-col-body">
              {items.map(o => (
                <div key={o.id} className="pipeline-card" onClick={() => navigate(`/opportunities/${o.id}`)}>
                  {o.involves_ai == 1 && <span className="ai-badge" style={{ marginBottom:4,display:'inline-block' }}>🤖 AI</span>}
                  <div className="pipeline-card-name">{o.name}</div>
                  {o.account_name && <div className="pipeline-card-account">🏢 {o.account_name}</div>}
                  {o.forecasted_tcv > 0 && <div className="pipeline-card-amount">${Number(o.forecasted_tcv).toLocaleString()}</div>}
                  <div style={{ display:'flex',gap:4,marginTop:4,flexWrap:'wrap' }}>
                    <Badge status={o.priority} />
                    {o.pursuit_score && <span style={{ fontSize:10,fontWeight:600,color: o.pursuit_score.pct>=75?'#16a34a':o.pursuit_score.pct>=50?'#f59e0b':'#dc2626' }}>{o.pursuit_score.pct}%</span>}
                  </div>
                </div>
              ))}
              {items.length === 0 && <div style={{ fontSize:12,color:'var(--gray-300)',textAlign:'center',padding:12 }}>Empty</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function Opportunities() {
  const [opps, setOpps] = useState([]); const [total, setTotal] = useState(0); const [page, setPage] = useState(1);
  const [search, setSearch] = useState(''); const [stage, setStage] = useState(''); const [priority, setPriority] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [view, setView] = useState('pipeline'); const [showForm, setShowForm] = useState(false); const [editOpp, setEditOpp] = useState(null);
  const [archiveId, setArchiveId] = useState(null); const [loading, setLoading] = useState(true);
  const navigate = useNavigate(); const { can } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/opportunities', { params: { search, stage, priority, year, is_active: showArchived ? '0' : '1', page, limit: view === 'pipeline' ? 200 : 25 } });
      setOpps(r.data.data); setTotal(r.data.total);
    } finally { setLoading(false); }
  }, [search, stage, priority, year, showArchived, page, view]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <div className="page-header">
        <span className="page-title">Opportunities</span>
        <div style={{ display:'flex',gap:8,alignItems:'center' }}>
          <div style={{ display:'flex',background:'var(--gray-100)',borderRadius:6,padding:2 }}>
            {['pipeline','list'].map(v => (
              <button key={v} onClick={() => setView(v)} style={{ padding:'5px 12px',border:'none',borderRadius:5,background:view===v?'white':'transparent',fontWeight:view===v?600:400,fontSize:12,cursor:'pointer',color:view===v?'var(--gray-900)':'var(--gray-500)',boxShadow:view===v?'var(--shadow)':'none' }}>
                {v === 'pipeline' ? '⬛ Pipeline' : '☰ List'}
              </button>
            ))}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowArchived(!showArchived)} style={{ color: showArchived ? 'var(--warning)' : undefined }}>
            {showArchived ? '← Active' : '📦 Archived'}
          </button>
          {can('edit') && <button className="btn btn-primary" onClick={() => { setEditOpp(null); setShowForm(true); }}>+ New Opportunity</button>}
        </div>
      </div>
      <div className="page-body">
        {showArchived && <div className="archive-banner">📦 Showing archived opportunities</div>}
        <div style={{ marginBottom:16 }}>
          <div className="toolbar">
            <input className="search-input" placeholder="Search by name or ID..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            <select className="filter-select" value={stage} onChange={e => { setStage(e.target.value); setPage(1); }}>
              <option value="">All Stages</option>{STAGES.map(s => <option key={s}>{s}</option>)}
            </select>
            <select className="filter-select" value={priority} onChange={e => { setPriority(e.target.value); setPage(1); }}>
              <option value="">All Priorities</option>{PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </select>
            <select className="filter-select" value={year} onChange={e => { setYear(e.target.value); setPage(1); }}>
              <option value="">All Years</option>
              {[2023,2024,2025,2026,2027,2028].map(y => <option key={y} value={String(y)}>{y}</option>)}
            </select>
            <span className="text-muted text-sm" style={{ marginLeft:'auto' }}>{total} opportunities</span>
          </div>
        </div>

        {view === 'pipeline' ? (
          <PipelineView opps={opps} navigate={navigate} />
        ) : (
          <div className="card">
            <div className="table-container">
              <table>
                <thead><tr><th>ID</th><th>Name</th><th>Account</th><th>Stage</th><th>Priority</th><th>TCV</th><th>Close</th><th>Year</th><th>Score</th><th></th></tr></thead>
                <tbody>
                  {loading && <tr><td colSpan={9} style={{ textAlign:'center',padding:40,color:'var(--gray-400)' }}>Loading...</td></tr>}
                  {!loading && opps.length === 0 && <tr><td colSpan={9}><div className="empty-state"><div className="empty-state-icon">💼</div><div className="empty-state-text">No opportunities found</div></div></td></tr>}
                  {opps.map(o => (
                    <tr key={o.id} onClick={() => navigate(`/opportunities/${o.id}`)}>
                      <td style={{ fontFamily:'monospace',fontSize:11,color:'var(--gray-500)' }}>{o.opportunity_id}</td>
                      <td>
                        <div style={{ display:'flex',alignItems:'center',gap:6 }}>
                          {o.involves_ai == 1 && <span className="ai-badge">🤖</span>}
                          <span className="td-link">{o.name}</span>
                        </div>
                      </td>
                      <td>{o.account_name||'—'}</td>
                      <td><Badge status={o.stage} /></td>
                      <td><Badge status={o.priority} /></td>
                      <td><Money value={o.forecasted_tcv} /></td>
                      <td>{fmtDate(o.expected_close_date)}</td><td style={{ fontSize:12,color:'var(--gray-500)' }}>{o.expected_close_date?String(o.expected_close_date).substring(0,4):'—'}</td>
                      <td>
                        {o.pursuit_score && <span style={{ fontSize:12,fontWeight:700,color:o.pursuit_score.pct>=75?'#16a34a':o.pursuit_score.pct>=50?'#f59e0b':'#dc2626' }}>{o.pursuit_score.pct}%</span>}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        {can('edit') && <div style={{ display:'flex',gap:4 }}>
                          <button className="btn btn-secondary btn-sm btn-icon" onClick={() => { setEditOpp(o); setShowForm(true); }}>✏️</button>
                          <button className="btn btn-secondary btn-sm btn-icon" title="Archive" onClick={() => setArchiveId(o.id)}>📦</button>
                        </div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} limit={25} total={total} onPage={setPage} />
          </div>
        )}
      </div>
      {showForm && <OppForm opp={editOpp} onSave={() => { setShowForm(false); load(); }} onClose={() => setShowForm(false)} />}
      {archiveId && <ConfirmDialog message="Archive this opportunity? It can be restored later." confirmLabel="Archive" onConfirm={async () => { await api.delete(`/opportunities/${archiveId}`); setArchiveId(null); load(); }} onCancel={() => setArchiveId(null)} />}
    </>
  );
}
